import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";

// カルーセル（Embla）

const PREFERS_REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const setupDots = (embla, root) => {
	const wrapper = root.querySelector("[data-carousel-dots]");
	if (!wrapper) return;

	const dots = embla.scrollSnapList().map((_, i) => {
		const dot = document.createElement("button");
		dot.type = "button";
		dot.className = "dot";
		dot.setAttribute("aria-label", `${i + 1}枚目を表示`);
		dot.addEventListener("click", () => embla.scrollTo(i));
		wrapper.appendChild(dot);
		return dot;
	});

	const update = () => {
		const selected = embla.selectedScrollSnap();
		dots.forEach((dot, i) => {
			dot.classList.toggle("is-active", i === selected);
			dot.setAttribute("aria-current", i === selected ? "true" : "false");
		});
	};

	embla.on("select", update).on("reInit", update);
	update();
};

const setupArrows = (embla, root) => {
	const prev = root.querySelector("[data-carousel-prev]");
	const next = root.querySelector("[data-carousel-next]");
	if (!prev && !next) return;

	if (prev) prev.addEventListener("click", () => embla.scrollPrev());
	if (next) next.addEventListener("click", () => embla.scrollNext());

	const update = () => {
		if (prev) prev.disabled = !embla.canScrollPrev();
		if (next) next.disabled = !embla.canScrollNext();
	};

	embla.on("select", update).on("reInit", update);
	update();
};

const setupCounter = (embla, root) => {
	const current = root.querySelector("[data-carousel-current]");
	const total = root.querySelector("[data-carousel-total]");
	if (!current && !total) return;

	let timer = 0;
	const update = () => {
		const next = embla.selectedScrollSnap() + 1;
		if (total) total.textContent = embla.scrollSnapList().length;
		if (!current) return;

		window.clearTimeout(timer);
		if (current.textContent !== String(next)) {
			current.classList.add("is-changing");
			timer = window.setTimeout(() => {
				current.textContent = next;
				current.classList.remove("is-changing");
			}, 120);
		} else {
			current.classList.remove("is-changing");
			current.textContent = next;
		}
	};

	embla.on("select", update).on("reInit", update);
	update();
};

const setupProgress = (embla, root) => {
	const bar = root.querySelector("[data-carousel-progress]");
	if (!bar) return;

	const { autoplay } = embla.plugins();
	if (!autoplay) return;

	const startFill = () => {
		bar.style.transition = "none";
		bar.style.transform = "scaleX(0)";

		window.requestAnimationFrame(() => {
			const remaining = autoplay.timeUntilNext();
			if (remaining === null || remaining <= 0) return;
			bar.style.transition = `transform ${remaining}ms linear`;
			bar.style.transform = "scaleX(1)";
		});
	};

	const freeze = () => {
		const { transform } = window.getComputedStyle(bar);
		bar.style.transition = "none";
		bar.style.transform = transform === "none" ? "scaleX(0)" : transform;
	};

	embla.on("autoplay:timerset", startFill);
	embla.on("autoplay:timerstopped", freeze);
	embla.on("select", () => {
		if (autoplay.isPlaying()) autoplay.reset();
	});

	if (autoplay.isPlaying()) startFill();
};

const setupThumbs = (embla, root) => {
	const thumbsRoot = root.querySelector("[data-carousel-thumbs]");
	if (!thumbsRoot) return;

	const viewport = thumbsRoot.querySelector("[data-carousel-thumbs-viewport]");
	const container = thumbsRoot.querySelector("[data-carousel-thumbs-container]");
	if (!viewport || !container) return;

	if (!container.children.length) {
		embla.slideNodes().forEach((slide, index) => {
			const img = slide.querySelector("img");
			const wrap = document.createElement("div");
			wrap.className = "slide";

			const btn = document.createElement("button");
			btn.type = "button";
			btn.className = "thumb";
			btn.setAttribute("aria-label", `${index + 1}枚目を表示`);

			if (img) {
				const thumbImg = document.createElement("img");
				thumbImg.src = img.currentSrc || img.src;
				thumbImg.alt = "";
				thumbImg.setAttribute("aria-hidden", "true");
				btn.appendChild(thumbImg);
			}

			btn.addEventListener("click", () => {
				embla.scrollTo(index);
				const { autoplay } = embla.plugins();
				if (autoplay) autoplay.reset();
			});
			wrap.appendChild(btn);
			container.appendChild(wrap);
		});
	}

	const thumbs = EmblaCarousel(viewport, {
		containScroll: "keepSnaps",
		dragFree: true,
		align: "start",
		duration: PREFERS_REDUCED_MOTION ? 0 : 25,
	});

	const prev = thumbsRoot.querySelector("[data-carousel-thumbs-prev]");
	const next = thumbsRoot.querySelector("[data-carousel-thumbs-next]");
	if (prev) prev.addEventListener("click", () => thumbs.scrollPrev());
	if (next) next.addEventListener("click", () => thumbs.scrollNext());

	const updateThumbsArrows = () => {
		if (prev) prev.disabled = !thumbs.canScrollPrev();
		if (next) next.disabled = !thumbs.canScrollNext();
	};

	const buttons = container.querySelectorAll(".thumb");
	const update = () => {
		const selected = embla.selectedScrollSnap();
		buttons.forEach((btn, i) => {
			btn.classList.toggle("is-active", i === selected);
		});
		thumbs.scrollTo(selected);
		updateThumbsArrows();
	};

	thumbs.on("select", updateThumbsArrows).on("reInit", updateThumbsArrows);
	embla.on("select", update).on("reInit", update);
	thumbs.reInit();
	update();
};

/**
 * フェード切り替え
 * @param {Element} root - [data-carousel][data-fade]要素
 */
const createFade = (root) => {
	const slides = [...root.querySelectorAll(".slide")];
	if (slides.length === 0) return null;

	const dotsWrap = root.querySelector("[data-carousel-dots]");
	const delay = Number(root.dataset.autoplay);
	const loop = typeof root.dataset.loop !== "undefined";
	let index = Math.max(
		0,
		slides.findIndex((slide) => slide.classList.contains("is-active")),
	);
	let timer = 0;
	let dots = [];
	let play = () => {};

	const apply = () => {
		slides.forEach((slide, i) => {
			slide.classList.toggle("is-active", i === index);
		});
		dots.forEach((dot, i) => {
			dot.classList.toggle("is-active", i === index);
			if (i === index) {
				dot.setAttribute("aria-current", "true");
			} else {
				dot.removeAttribute("aria-current");
			}
		});
	};

	const stop = () => {
		window.clearInterval(timer);
		timer = 0;
	};

	const goTo = (next, fromUser = false) => {
		if (loop) {
			index = ((next % slides.length) + slides.length) % slides.length;
		} else {
			index = Math.max(0, Math.min(slides.length - 1, next));
		}
		apply();
		if (fromUser) play();
	};

	play = () => {
		stop();
		if (!delay || PREFERS_REDUCED_MOTION || slides.length < 2) return;
		timer = window.setInterval(() => {
			goTo(index + 1);
		}, delay);
	};

	if (dotsWrap && slides.length > 1) {
		dots = slides.map((_, i) => {
			const dot = document.createElement("button");
			dot.type = "button";
			dot.className = "dot";
			dot.setAttribute("aria-label", `${i + 1}枚目を表示`);
			dot.addEventListener("click", () => goTo(i, true));
			dotsWrap.appendChild(dot);
			return dot;
		});
	} else if (dotsWrap) {
		dotsWrap.hidden = true;
	}

	apply();
	play();

	if (slides.length > 1) {
		const SWIPE_PX = 50;
		let startX = 0;
		let startY = 0;
		let dragging = false;

		root.addEventListener("pointerdown", (event) => {
			if (event.button && event.button !== 0) return;
			if (event.target.closest("button")) return;
			startX = event.clientX;
			startY = event.clientY;
			dragging = true;
			root.setPointerCapture(event.pointerId);
		});

		const endSwipe = (event) => {
			if (!dragging) return;
			dragging = false;
			const dx = event.clientX - startX;
			const dy = event.clientY - startY;
			if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
			goTo(index + (dx < 0 ? 1 : -1), true);
		};

		root.addEventListener("pointerup", endSwipe);
		root.addEventListener("pointercancel", () => {
			dragging = false;
		});
	}

	root.addEventListener("mouseenter", stop);
	root.addEventListener("mouseleave", play);
	root.addEventListener("focusin", stop);
	root.addEventListener("focusout", (event) => {
		if (!root.contains(event.relatedTarget)) play();
	});

	return { scrollTo: (i) => goTo(i, true) };
};

/**
 * @param {Element} root - [data-carousel]要素
 */
const create = (root) => {
	if (typeof root.dataset.fade !== "undefined") {
		return createFade(root);
	}

	const viewport = root.querySelector("[data-carousel-viewport]") || root;
	const { loop, autoplay, align } = root.dataset;

	const plugins = [];
	if (typeof autoplay !== "undefined" && !PREFERS_REDUCED_MOTION) {
		plugins.push(
			Autoplay({
				delay: Number(autoplay) || 3000,
				stopOnInteraction: false,
				stopOnMouseEnter: false,
				rootNode: () => root,
			}),
		);
	}

	const embla = EmblaCarousel(
		viewport,
		{
			loop: typeof loop !== "undefined",
			align: align || "start",
			duration: PREFERS_REDUCED_MOTION ? 0 : 25,
		},
		plugins,
	);

	setupArrows(embla, root);
	setupDots(embla, root);
	setupCounter(embla, root);
	setupProgress(embla, root);
	setupThumbs(embla, root);

	return embla;
};

/**
 * [data-carousel]を初期化
 * @param {Element|Document} scope - 探索範囲
 * @returns {Array} 生成したEmblaインスタンス
 */
export const initCarousels = (scope = document) => {
	const roots = scope.querySelectorAll("[data-carousel]:not([data-carousel-ready])");
	return [...roots].map((root) => {
		root.setAttribute("data-carousel-ready", "");
		return create(root);
	});
};

export default initCarousels;
